/**
 * A type alias for a constructor function.
 *
 * @template T - The type of the object that the constructor creates.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Constructor<T> = { new (...args: any[]): T };

/**
 * A dependency injection container implemented as a singleton.
 */
export class DependencyInjectionContainer {
  private static instance: DependencyInjectionContainer;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private services: Map<string, any> = new Map();

  /**
   * Private constructor to prevent direct instantiation.
   */
  private constructor() {
    // Empty on purpose.
  }

  /**
   * Get the single instance of the DIContainer.
   *
   * @returns {DIContainer} The single instance of the DIContainer.
   */
  public static getInstance(): DependencyInjectionContainer {
    if (!DependencyInjectionContainer.instance) {
      DependencyInjectionContainer.instance =
        new DependencyInjectionContainer();
    }
    return DependencyInjectionContainer.instance;
  }

  /**
   * Register a service with a specific name.
   *
   * @template T - The type of the service.
   * @param {string} name - The name to register the service under.
   * @param {Constructor<T>} service - The constructor of the service.
   */
  public register<T>(name: string, service: Constructor<T>): void {
    if (!this.services.has(name)) {
      this.services.set(name, new service({}));
    }
  }

  /**
   * Resolve a service by name.
   *
   * @template T - The type of the service.
   * @param {string} name - The name of the service to resolve.
   * @returns {T} The resolved service instance.
   * @throws {Error} If the service is not found in the DIContainer.
   */
  public resolve<T>(name: string): T {
    const service = this.services.get(name);
    if (!service) {
      throw new Error(
        `Service ${name} not found in DependencyInjectionContainer.`,
      );
    }
    return service;
  }
}

// Usage:
// Get the single instance of the DIContainer
// const container = DIContainer.getInstance();

// Register services
// container.register('logger', Logger);
// const logger = container.resolve<Logger>('logger');

// const logger = container.resolve<Logger>('logger');
// container.register('apiService', class {
//   constructor() {
//     return new ApiService(logger);
//   }
// });
